import { getAction, postAction, postRequest } from "@/api/common";

const state = {
  applymentDetail: {}, // 申请单详情
  applymentSupplierList: [], // 申请单关联供应商列表
  applymentHasRegisteredAddress: false, // 申请单是否有“公司注册地址”服务
  applymentHasNomineeDirector: false, // 申请单是否有“名义董事”服务
  applymentHasEPApplication: false, // 申请单是否有“EP申请”服务
  applymentHasCompanySecretary: false, // 申请单是否有“公司秘书”服务
};

const mutations = {
  // 设置申请单详情
  SET_APPLYMENT_DETAIL(state, detail) {
    state.applymentDetail = detail;
  },
  // 设置申请单详情中的单个属性
  SET_APPLYMENT_DETAIL_FIELD(state, { key, value }) {
    state.applymentDetail[key] = value;
  },
  // 设置申请单包含的服务
  SET_APPLYMENT_INCLUDED_SERVICES(state, payload) {
    // 先重置
    state.applymentHasRegisteredAddress = false;
    state.applymentHasNomineeDirector = false;
    state.applymentHasEPApplication = false;
    state.applymentHasCompanySecretary = false;
    // 遍历商品列表，判断是否有服务
    const { detail, serviceCodeMap } = payload;
    const goodsList = detail.goodsList || [];
    const serviceCodeList = [];
    goodsList.forEach(ele => {
      const productType = ele.productType;
      if (productType == "package") {
        // 套餐
        const serviceList = ele[productType]?.serviceList || [];
        const serviceCodeArr = serviceList.map(item => item.serviceCode);
        serviceCodeList.push(...serviceCodeArr);
      } else {
        // 自定义套餐
        const serviceCode = ele[productType]?.serviceCode || "";
        if (serviceCode) serviceCodeList.push(serviceCode);
      }
    });
    serviceCodeList.forEach(code => {
      // 公司注册地址服务code列表
      const companyRegistrationAddressServiceCodeList = serviceCodeMap?.companyRegistrationAddressServiceCodeList || [];
      if (companyRegistrationAddressServiceCodeList.includes(code)) {
        state.applymentHasRegisteredAddress = true;
      }
      // 名义董事服务code列表
      const nomineeDirectorServiceCodeList = serviceCodeMap?.nomineeDirectorServiceCodeList || [];
      if (nomineeDirectorServiceCodeList.includes(code)) {
        state.applymentHasNomineeDirector = true;
      }
      // 就业准证(EP)申请服务code列表
      const epApplicationServiceCodeList = serviceCodeMap?.epApplicationServiceCodeList || [];
      if (epApplicationServiceCodeList.includes(code)) {
        state.applymentHasEPApplication = true;
      }
      // 公司秘书服务code列表
      const companySecretaryServiceCodeList = serviceCodeMap?.companySecretaryServiceCodeList || [];
      if (companySecretaryServiceCodeList.includes(code)) {
        state.applymentHasCompanySecretary = true;
      }
    });
  },
  // 设置申请单关联供应商列表
  SET_APPLYMENT_SUPPLIER_LIST(state, list) {
    state.applymentSupplierList = list;
  },
  // 重置申请单相关状态(接口失败时调用，避免展示旧数据)
  RESET_APPLYMENT_STATE(state) {
    state.applymentDetail = {};
    state.applymentSupplierList = [];
    state.applymentHasRegisteredAddress = false;
    state.applymentHasNomineeDirector = false;
    state.applymentHasEPApplication = false;
    state.applymentHasCompanySecretary = false;
  },
};

const actions = {
  // 获取申请单详情
  fetchApplymentDetail({ commit, rootGetters }, id) {
    return new Promise((resolve, reject) => {
      getAction(`/companyApplyment/${id}/detail`)
        .then(res => {
          const resData = res.data || {};
          commit("SET_APPLYMENT_DETAIL", resData);
          const serviceCodeMap = rootGetters["constant/getServiceCodeMap"](resData.country, resData.businessType);
          commit("SET_APPLYMENT_INCLUDED_SERVICES", { detail: resData, serviceCodeMap });
          resolve(resData);
        })
        .catch(err => {
          // 接口失败时重置状态，避免展示上一个申请单的旧数据
          commit("RESET_APPLYMENT_STATE");
          reject(err);
        });
    });
  },
  // 获取申请单详情 - 后台用户专用
  fetchAdminApplymentDetail({ commit, rootGetters }, params) {
    return new Promise((resolve, reject) => {
      getAction(`/companyApplymentDetail/${params.id}/detail?key=${params.key}`)
        .then(res => {
          const resData = res.data || {};
          commit("SET_APPLYMENT_DETAIL", resData);
          const serviceCodeMap = rootGetters["constant/getServiceCodeMap"](resData.country, resData.businessType);
          commit("SET_APPLYMENT_INCLUDED_SERVICES", { detail: resData, serviceCodeMap });
          resolve(resData);
        })
        .catch(err => {
          // 接口失败时重置状态，避免展示上一个申请单的旧数据
          commit("RESET_APPLYMENT_STATE");
          reject(err);
        });
    });
  },
  // 获取申请单关联供应商列表
  fetchApplymentSupplierList({ commit, state }) {
    return new Promise((resolve, reject) => {
      const { id } = state.applymentDetail;
      getAction(`/companyApplymentSupplierQuery/getList`, {
        applymentId: id,
      })
        .then(res => {
          const resData = res.data || [];
          commit("SET_APPLYMENT_SUPPLIER_LIST", resData);
          resolve(resData);
        })
        .catch(err => {
          commit("SET_APPLYMENT_SUPPLIER_LIST", []);
          reject(err);
        });
    });
  },
  // 保存申请单
  saveApplyment({ commit, state }, params) {
    return new Promise((resolve, reject) => {
      const { id, applymentStatus, version = 0 } = state.applymentDetail;
      postRequest(`/companyApplyment/${id}/save`, { ...params, applymentStatus, version })
        .then(res => {
          const resData = res.data || {};
          // 版本号+1
          commit("SET_APPLYMENT_DETAIL_FIELD", { key: "version", value: version + 1 });
          resolve(resData);
        })
        .catch(err => {
          reject(err);
        });
    });
  },
  // 申请单资料已修正
  correctApplyment({ state }) {
    return new Promise((resolve, reject) => {
      const { id } = state.applymentDetail;
      postAction(`/companyApplyment/${id}/correct`)
        .then(res => {
          const resData = res.data || {};
          resolve(resData);
        })
        .catch(err => {
          reject(err);
        });
    });
  },
  // 提交申请单
  commitApplyment({ state }) {
    return new Promise((resolve, reject) => {
      const { id } = state.applymentDetail;
      postAction(`/companyApplyment/${id}/commit`)
        .then(res => {
          const resData = res.data || {};
          resolve(resData);
        })
        .catch(err => {
          reject(err);
        });
    });
  },
  // 申请单咨询/问价
  inquireApplyment({ state }, params) {
    return new Promise((resolve, reject) => {
      const { id } = state.applymentDetail;
      postRequest(`/companyApplyment/${id}/inquire`, params)
        .then(res => {
          const resData = res.data || {};
          resolve(resData);
        })
        .catch(err => {
          reject(err);
        });
    });
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
